import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, User, Menu, X, LogOut, LayoutDashboard, ChevronDown, ChevronRight, Heart } from 'lucide-react';
import Logo from './Logo';
import { NAV_LINKS, INSTITUTION_LINKS } from '../brand';
import { useCatalogNav } from '../hooks/useSiteData';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import { Button } from './ui/button';

const announcement = 'Free Shipping on Orders above ₹500 • Ships from Bengaluru • Rare in Nature. Local at Heart.';

function AnnouncementBar() {
  const repeated = Array(3).fill(announcement);
  return (
    <div aria-hidden="true" className="overflow-hidden bg-brand-dark text-brand-gold-light text-[10px] sm:text-xs py-1.5 sm:py-2 gold-foil-bottom">
      <div className="marquee-track">
        {[...repeated, ...repeated].map((t, i) => (
          <span key={i} className="px-5 sm:px-8 tracking-[0.15em] sm:tracking-[0.2em] uppercase">{t}</span>
        ))}
      </div>
    </div>
  );
}

/* Desktop dropdown wrapper — opens on hover / focus, closes on Escape */
function Dropdown({ label, children, align = 'left' }) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  const openNow = () => { clearTimeout(timerRef.current); setOpen(true); };
  const closeSoon = () => { clearTimeout(timerRef.current); timerRef.current = setTimeout(() => setOpen(false), 120); };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div ref={wrapperRef} className="relative" onMouseEnter={openNow} onMouseLeave={closeSoon} onFocus={openNow} onBlur={(e) => { if (!wrapperRef.current?.contains(e.relatedTarget)) closeSoon(); }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs xl:text-[13px] uppercase tracking-widest font-medium whitespace-nowrap transition-colors text-brand-gold-light hover:text-brand-gold inline-flex items-center gap-1 py-2 focus:outline-none focus-visible:text-brand-gold"
        aria-expanded={open}
        aria-haspopup="true"
      >
        {label}
        <ChevronDown size={12} aria-hidden="true" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`absolute top-full ${align === 'right' ? 'right-0' : 'left-0'} mt-1 min-w-[220px] bg-white shadow-2xl rounded-lg overflow-visible border border-brand-gold/15 py-2 z-50`} role="menu">
          {children}
        </div>
      )}
    </div>
  );
}

/* One category row; if it has children, hovering reveals a nested panel */
function CategoryRow({ node, onNavigate }) {
  const [subOpen, setSubOpen] = useState(false);
  if (!node.children) {
    return (
      <NavLink
        role="menuitem"
        to={`/shop/${node.slug}`}
        onClick={onNavigate}
        className={({ isActive }) => `block px-4 py-2 text-sm hover:bg-brand-ivory focus:outline-none focus-visible:bg-brand-ivory ${isActive ? 'text-brand-gold font-medium' : 'text-brand-dark'}`}
      >
        {node.label}
      </NavLink>
    );
  }
  return (
    <div className="relative" onMouseEnter={() => setSubOpen(true)} onMouseLeave={() => setSubOpen(false)}>
      <NavLink
        role="menuitem"
        to={`/shop/${node.slug}`}
        onClick={onNavigate}
        onFocus={() => setSubOpen(true)}
        className={({ isActive }) => `flex items-center justify-between px-4 py-2 text-sm hover:bg-brand-ivory focus:outline-none focus-visible:bg-brand-ivory ${isActive ? 'text-brand-gold font-medium' : 'text-brand-dark'}`}
      >
        {node.label}
        <ChevronRight size={12} aria-hidden="true" className="text-brand-green/50" />
      </NavLink>
      {subOpen && (
        <div className="absolute top-0 left-full ml-1 min-w-[220px] bg-white shadow-2xl rounded-lg border border-brand-gold/15 py-2 z-50" role="menu">
          {node.children.map((c) => (
            <NavLink
              key={c.slug}
              role="menuitem"
              to={`/shop/${c.slug}`}
              onClick={onNavigate}
              className={({ isActive }) => `block px-4 py-2 text-sm hover:bg-brand-ivory ${isActive ? 'text-brand-gold font-medium' : 'text-brand-dark'}`}
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const CATEGORY_TREE = useCatalogNav();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [userMenu, setUserMenu] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState('');
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const { count: wishCount } = useWishlist();
  const navigate = useNavigate();
  const menuBtnRef = useRef(null);
  const drawerRef = useRef(null);

  useEffect(() => {
    if (!mobileOpen) return;
    const openerBtn = menuBtnRef.current;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', onKey);
    setTimeout(() => drawerRef.current?.querySelector('a, button')?.focus(), 50);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', onKey);
      openerBtn?.focus();
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!userMenu) return;
    const onKey = (e) => { if (e.key === 'Escape') setUserMenu(false); };
    const onClick = (e) => { if (!e.target.closest('[data-user-menu]')) setUserMenu(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onClick);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('click', onClick); };
  }, [userMenu]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) navigate(`/shop?search=${encodeURIComponent(searchQ.trim())}`);
    setSearchOpen(false);
  };

  const closeMobile = () => setMobileOpen(false);

  const navLinkClass = ({ isActive }) => `text-xs xl:text-[13px] uppercase tracking-widest font-medium whitespace-nowrap transition-colors py-2 ${isActive ? 'text-brand-gold' : 'text-brand-gold-light hover:text-brand-gold'}`;

  return (
    <header className="relative z-40 shadow-sm">
      <AnnouncementBar />
      <div className="bg-brand-dark gold-foil-bottom">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 md:h-24 flex items-center justify-between gap-4 lg:gap-8">
          {/* Mobile: hamburger + logo grouped on the left | Desktop: logo alone left, nav after */}
          <div className="flex items-center gap-2 min-w-0 lg:flex-shrink">
            <button
              ref={menuBtnRef}
              className="lg:hidden text-brand-gold min-w-[44px] min-h-[44px] flex items-center justify-center -ml-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
            >
              <Menu size={24} aria-hidden="true" />
            </button>

            <Link to="/" className="flex-shrink min-w-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-md" aria-label="Rare Locals home">
              <span className="lg:hidden"><Logo size="mobile-lg" /></span>
              <span className="hidden lg:block"><Logo size="md" /></span>
            </Link>
          </div>

          <nav aria-label="Primary" className="hidden lg:flex items-center gap-3 xl:gap-5">
            <NavLink to="/" end className={navLinkClass}>Home</NavLink>
            <NavLink to="/shop" end className={navLinkClass}>Shop All</NavLink>
            <Dropdown label="Category">
              {CATEGORY_TREE.map((n) => <CategoryRow key={n.slug} node={n} />)}
            </Dropdown>
            <Dropdown label="Bulk & B2B">
              {INSTITUTION_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  role="menuitem"
                  to={l.to}
                  className={({ isActive }) => `block px-4 py-2 text-sm hover:bg-brand-ivory ${isActive ? 'text-brand-gold font-medium' : 'text-brand-dark'}`}
                >
                  {l.label}
                </NavLink>
              ))}
            </Dropdown>
            <NavLink to="/blog" className={navLinkClass}>Blog</NavLink>
            <NavLink to="/about" className={navLinkClass}>Our Story</NavLink>
            <NavLink to="/contact" className={navLinkClass}>Contact</NavLink>
          </nav>

          <div className="flex items-center gap-1 sm:gap-2 text-brand-gold-light flex-shrink-0">
            <button onClick={() => setSearchOpen((v) => !v)} className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md hover:text-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" aria-label="Search" aria-expanded={searchOpen}>
              <Search size={20} aria-hidden="true" />
            </button>
            <Link to="/wishlist" className="relative min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md hover:text-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" aria-label={`Wishlist (${wishCount})`}>
              <Heart size={20} aria-hidden="true" />
              {wishCount > 0 && (
                <span aria-hidden="true" className="absolute top-1 right-1 bg-brand-gold text-brand-dark text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-semibold">{wishCount}</span>
              )}
            </Link>
            {user ? (
              <div className="relative" data-user-menu>
                <button
                  onClick={() => setUserMenu((v) => !v)}
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center gap-1 rounded-md hover:text-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                  aria-label="Account menu"
                  aria-expanded={userMenu}
                  aria-haspopup="menu"
                >
                  <User size={20} aria-hidden="true" />
                  <ChevronDown size={14} aria-hidden="true" />
                </button>
                {userMenu && (
                  <div role="menu" className="absolute right-0 mt-2 w-56 bg-white shadow-xl rounded-lg overflow-hidden text-brand-dark">
                    <div className="px-4 py-3 border-b border-brand-gold/20">
                      <div className="font-medium text-sm">{user.name}</div>
                      <div className="text-xs text-brand-green/70 truncate">{user.email || user.phone}</div>
                    </div>
                    <Link role="menuitem" to="/account" onClick={() => setUserMenu(false)} className="block px-4 py-2 text-sm hover:bg-brand-ivory">My Orders</Link>
                    <Link role="menuitem" to="/wishlist" onClick={() => setUserMenu(false)} className="block px-4 py-2 text-sm hover:bg-brand-ivory">My Wishlist</Link>
                    {(user.role === 'admin' || user.role === 'store') && (
                      <Link role="menuitem" to="/admin" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-brand-ivory">
                        <LayoutDashboard size={14} aria-hidden="true" /> Admin Panel
                      </Link>
                    )}
                    <button role="menuitem" onClick={() => { logout(); setUserMenu(false); }} className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-brand-ivory text-left">
                      <LogOut size={14} aria-hidden="true" /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md hover:text-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" aria-label="Sign in">
                <User size={20} aria-hidden="true" />
              </Link>
            )}
            <button onClick={() => setOpen(true)} className="relative min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md hover:text-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" aria-label={`Cart (${count} item${count === 1 ? '' : 's'})`}>
              <ShoppingBag size={20} aria-hidden="true" />
              {count > 0 && (
                <span aria-hidden="true" className="absolute top-1 right-1 bg-brand-gold text-brand-dark text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-semibold">{count}</span>
              )}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-brand-gold/15">
            <form onSubmit={submitSearch} className="max-w-7xl mx-auto px-4 lg:px-8 py-4 flex items-center gap-3">
              <Search size={18} className="text-brand-gold" aria-hidden="true" />
              <input
                autoFocus
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search coffee, tea, dehydrated..."
                aria-label="Search products"
                className="flex-1 bg-transparent border-b border-brand-gold/30 py-2 outline-none text-brand-gold-light placeholder:text-brand-gold-light/40 text-base sm:text-sm"
              />
              <Button type="submit" className="btn-gold">Search</Button>
            </form>
          </div>
        )}
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Site navigation">
          <div className="absolute inset-0 bg-black/60" onClick={closeMobile} aria-hidden="true" />
          <div id="mobile-nav" ref={drawerRef} className="absolute left-0 top-0 bottom-0 w-80 max-w-[88vw] bg-brand-dark p-6 overflow-y-auto shadow-2xl" style={{ paddingLeft: 'max(1.5rem, env(safe-area-inset-left))' }}>
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-brand-gold/20">
              <Logo size="md" />
              <button onClick={closeMobile} className="text-brand-gold min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" aria-label="Close menu">
                <X size={22} aria-hidden="true" />
              </button>
            </div>
            <nav aria-label="Mobile primary" className="flex flex-col">
              <NavLink to="/" end onClick={closeMobile} className={({ isActive }) => `py-3 px-2 border-b border-brand-gold/10 text-sm uppercase tracking-widest font-medium ${isActive ? 'text-brand-gold' : 'text-brand-gold-light'}`}>Home</NavLink>
              <NavLink to="/shop" end onClick={closeMobile} className={({ isActive }) => `py-3 px-2 border-b border-brand-gold/10 text-sm uppercase tracking-widest font-medium ${isActive ? 'text-brand-gold' : 'text-brand-gold-light'}`}>Shop All</NavLink>

              {/* Category collapsible */}
              <button type="button" onClick={() => setExpandedMobile(expandedMobile === 'cat' ? '' : 'cat')} className="flex items-center justify-between py-3 px-2 border-b border-brand-gold/10 text-sm uppercase tracking-widest font-medium text-brand-gold-light" aria-expanded={expandedMobile === 'cat'}>
                Category <ChevronDown size={14} className={`transition-transform ${expandedMobile === 'cat' ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {(expandedMobile === 'cat' || expandedMobile.startsWith('cat-')) && (
                <div className="border-b border-brand-gold/10">
                  {CATEGORY_TREE.map((n) => (
                    <div key={n.slug}>
                      {n.children ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setExpandedMobile(expandedMobile === `cat-${n.slug}` ? 'cat' : `cat-${n.slug}`)}
                            className="w-full flex items-center justify-between py-2.5 pl-5 pr-2 text-xs uppercase tracking-widest text-brand-gold-light hover:text-brand-gold"
                            aria-expanded={expandedMobile === `cat-${n.slug}`}
                          >
                            <span>{n.label}</span>
                            <ChevronDown size={14} aria-hidden="true" className={`transition-transform ${expandedMobile === `cat-${n.slug}` ? 'rotate-180' : ''}`} />
                          </button>
                          {expandedMobile === `cat-${n.slug}` && (
                            <div className="pl-8 pb-2 space-y-1">
                              <NavLink onClick={closeMobile} to={`/shop/${n.slug}`} className="block py-1.5 text-[11px] tracking-wider text-brand-gold-light/80 hover:text-brand-gold">All {n.label}</NavLink>
                              {n.children.map((c) => (
                                <NavLink key={c.slug} onClick={closeMobile} to={`/shop/${c.slug}`} className="block py-1.5 text-[11px] tracking-wider text-brand-gold-light/80 hover:text-brand-gold">{c.label}</NavLink>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <NavLink onClick={closeMobile} to={`/shop/${n.slug}`} className="block py-2.5 pl-5 pr-2 text-xs uppercase tracking-widest text-brand-gold-light hover:text-brand-gold">{n.label}</NavLink>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Institution collapsible */}
              <button type="button" onClick={() => setExpandedMobile(expandedMobile === 'inst' ? '' : 'inst')} className="flex items-center justify-between py-3 px-2 border-b border-brand-gold/10 text-sm uppercase tracking-widest font-medium text-brand-gold-light" aria-expanded={expandedMobile === 'inst'}>
                Bulk & B2B <ChevronDown size={14} className={`transition-transform ${expandedMobile === 'inst' ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {expandedMobile === 'inst' && (
                <div className="pl-3 border-b border-brand-gold/10">
                  {INSTITUTION_LINKS.map((l) => (
                    <NavLink key={l.to} onClick={closeMobile} to={l.to} className="block py-2 text-xs uppercase tracking-widest text-brand-gold-light hover:text-brand-gold">{l.label}</NavLink>
                  ))}
                </div>
              )}

              {NAV_LINKS.filter((l) => !['Home', 'Shop All'].includes(l.label)).map((l) => (
                <NavLink key={l.to} to={l.to} onClick={closeMobile} className={({ isActive }) => `py-3 px-2 border-b border-brand-gold/10 text-sm uppercase tracking-widest font-medium ${isActive ? 'text-brand-gold' : 'text-brand-gold-light'}`}>{l.label}</NavLink>
              ))}
              <NavLink to="/wishlist" onClick={closeMobile} className="py-3 px-2 border-b border-brand-gold/10 text-sm uppercase tracking-widest font-medium text-brand-gold-light">Wishlist</NavLink>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
